import React, { useContext } from "react";
import type { ExternalNode } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";

import { OrgContext } from "contexts/OrgContext";
import { useAPIKeyStep } from "./hooks/useAPIKeyStep";
import { useBrevConnectNodePolling } from "./hooks/useBrevConnectNodePolling";
import { useComputeName } from "./hooks/useComputeName";
import { APIKeyStep } from "./steps/APIKeyStep";
import { ComputeNameStep } from "./steps/ComputeNameStep";
import { RegisterCommandStep } from "./steps/RegisterCommandStep";
import { WaitingForDevice } from "./components";
import { buildRegisterComputeCommand } from "./utils";

const COMMAND_PLACEHOLDER =
  "Complete the previous two steps to generate a command to connect your device";

export const ConnectDevice: React.FC<{
  copied: boolean;
  everCopied: boolean;
  registrationToken: string;
  onCopy: (command: string) => void;
  onRegistered: (node: ExternalNode) => void;
}> = ({ copied, everCopied, registrationToken, onCopy, onRegistered }) => {
  const { activeOrg, activeOrgId } = useContext(OrgContext);
  const computeNameStep = useComputeName(activeOrgId);

  const apiKeyStep = useAPIKeyStep({
    activeOrgId,
    computeName: computeNameStep.computeName,
    getComputeNameError: computeNameStep.getComputeNameError,
    hasValidName: computeNameStep.hasValidName,
    onComputeNameChange: computeNameStep.handleComputeNameChange,
    onComputeNameValidationError: computeNameStep.setSubmitNameError,
  });

  const { isError: isNodePollingError } = useBrevConnectNodePolling({
    registrationToken: everCopied ? registrationToken : null,
    enabled: everCopied,
    onNodeFound: onRegistered,
  });

  const orgDisplayName = activeOrg?.name?.trim() || "your organization";
  const canCopyCommand =
    computeNameStep.hasValidName && apiKeyStep.hasValidAPIKey;
  const registerCommand = computeNameStep.hasValidName
    ? buildRegisterComputeCommand(
        computeNameStep.computeName,
        apiKeyStep.newAPIKey,
        registrationToken,
      )
    : "";

  const copyRegisterCommand = () => {
    if (!canCopyCommand) return;
    onCopy(registerCommand);
  };

  return (
    <div
      className={`grid items-start gap-8 animate-fade-in-fast ${
        everCopied
          ? "lg:grid-cols-[minmax(0,700px)_1px_minmax(280px,1fr)] lg:gap-12"
          : ""
      }`}
    >
      <div className="flex w-full min-w-0 max-w-[700px] flex-col gap-6">
        <ComputeNameStep
          computeName={computeNameStep.computeName}
          isSettled={computeNameStep.isSettled}
          nameError={computeNameStep.nameError}
          onNameChange={apiKeyStep.handleComputeNameChange}
          onValidate={computeNameStep.handleValidateName}
        />
        <APIKeyStep
          activeOrgId={activeOrgId}
          apiKeyName={apiKeyStep.apiKeyName}
          canCreateNewKey={apiKeyStep.canCreateNewKey}
          createKeyError={apiKeyStep.createKeyError}
          createNewAPIKey={apiKeyStep.createNewAPIKey}
          hasCompletedStepOne={computeNameStep.isReady}
          hasValidAPIKey={apiKeyStep.hasValidAPIKey}
          isCreatingKey={apiKeyStep.isCreatingKey}
          isEditingAPIKeyName={apiKeyStep.isEditingAPIKeyName}
          isEditingKeyExpirationDate={apiKeyStep.isEditingKeyExpirationDate}
          isKeyExpirationDatePickerOpen={
            apiKeyStep.isKeyExpirationDatePickerOpen
          }
          keyExpirationDate={apiKeyStep.keyExpirationDate}
          keyExpirationDateLabel={apiKeyStep.keyExpirationDateLabel}
          newAPIKey={apiKeyStep.newAPIKey}
          onAPIKeyNameChange={apiKeyStep.onAPIKeyNameChange}
          onKeyExpirationDateChange={apiKeyStep.handleKeyExpirationDateChange}
          onKeyExpirationDatePickerOpenChange={
            apiKeyStep.onKeyExpirationDatePickerOpenChange
          }
          onStartEditingAPIKeyName={apiKeyStep.onStartEditingAPIKeyName}
          onStartEditingKeyExpirationDate={
            apiKeyStep.onStartEditingKeyExpirationDate
          }
          orgDisplayName={orgDisplayName}
        />
        <RegisterCommandStep
          canCopyCommand={canCopyCommand}
          commandPlaceholder={COMMAND_PLACEHOLDER}
          copied={copied}
          everCopied={everCopied}
          hasCompletedStepTwo={apiKeyStep.hasCompletedStepTwo}
          onCopy={copyRegisterCommand}
          registerCommand={registerCommand}
        />
      </div>
      {everCopied ? (
        <div className="h-px w-full bg-zinc-900 lg:h-full lg:w-px lg:min-h-[260px]" />
      ) : null}
      {everCopied ? (
        <WaitingForDevice withStatus isPollingError={isNodePollingError} />
      ) : null}
    </div>
  );
};

export default ConnectDevice;
